import { useBackendApi } from './backendApi';
import { useEventsPublisher } from 'event-bus';
import { VerificationStatus } from './emailDomainsService';
import { useObjectDatesMapper } from './Helper';

export interface SetSenderEmailRequest {
  email: string;
}

export interface SenderEmail {
  email: string;
  status: VerificationStatus;
  createdAt: Date;
  canVerificationCodeBeSent: boolean;
  verificationCodeCanBeSentAt?: Date
}

export interface SetVerificationCodeRequest {
  verificationCode: number,
}

export const SENDER_EMAIL_UPDATED = 'SENDER_EMAIL_UPDATED';

export function useSenderEmailService() {
  const nekstApi = useBackendApi();
  const eventsPublisher = useEventsPublisher();

  const objectMapper = useObjectDatesMapper<SenderEmail>(
    [],
    ['verificationCodeCanBeSentAt', 'createdAt']
  );

  const setCurrentUserSenderEmail = async (request: SetSenderEmailRequest): Promise<void> => {
    await nekstApi.put('/me/senderemail', request);
    eventsPublisher.publish(SENDER_EMAIL_UPDATED, {});
  };

  const setSenderEmail = async (id: number, request: SetSenderEmailRequest): Promise<SenderEmail> => {
    const result = await nekstApi.put(`/teammembers/${id}/senderemail`, request);
    eventsPublisher.publish(SENDER_EMAIL_UPDATED, {});

    return objectMapper.mapSingle(result);
  };

  const resendCurrentUserCode = async (): Promise<void> => {
    await nekstApi.post('/me/senderemail/resend');
    eventsPublisher.publish(SENDER_EMAIL_UPDATED, {});
  };

  const resendCode = async (id: number): Promise<SenderEmail> => {
    const result = await nekstApi.post(`/teammembers/${id}/senderemail/resend`) as SenderEmail;
    eventsPublisher.publish(SENDER_EMAIL_UPDATED, {});

    return objectMapper.mapSingle(result);
  };

  const getCurrentUserSenderEmail = async (): Promise<SenderEmail | null> => {
    const result = await nekstApi.get('/me/senderemail') as SenderEmail;

    if (result?.status === VerificationStatus.VERIFIED) {
      return objectMapper.mapSingle(result);
    } else {
      return null;
    }
  };

  const getPersonSenderEmail = async (id: number): Promise<SenderEmail | null> => {
    const result = await nekstApi.get(`/teammembers/${id}/senderemail`) as SenderEmail;

    if (result?.status === VerificationStatus.VERIFIED) {
      return objectMapper.mapSingle(result);
    } else {
      return null;
    }
  };

  const deletePersonSenderEmail = async (id: number): Promise<void> => {
    await nekstApi.delete(`/teammembers/${id}/senderemail`);
    eventsPublisher.publish(SENDER_EMAIL_UPDATED, {});
  };

  const deleteCurrentUserSenderEmail = async (): Promise<void> => {
    await nekstApi.delete('/me/senderemail');
    eventsPublisher.publish(SENDER_EMAIL_UPDATED, {});
  };

  const verifyCode = async (
    personId: number,
    request: SetVerificationCodeRequest
  ): Promise<SenderEmail> => {
    const result = await nekstApi.post(`/teammembers/${personId}/senderemail/verify`, request) as SenderEmail;

    eventsPublisher.publish(SENDER_EMAIL_UPDATED, {});

    return objectMapper.mapSingle(result);
  };

  return {
    setCurrentUserSenderEmail,
    setSenderEmail,
    resendCurrentUserCode,
    resendCode,
    getCurrentUserSenderEmail,
    getPersonSenderEmail,
    deletePersonSenderEmail,
    deleteCurrentUserSenderEmail,
    verifyCode,
  };
}
